import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { clearToken, exchangeSso, getToken, getUserName } from "./api";
import LoginScreen from "./marketing/LoginScreen";
import { MarketingContext, useMarketing } from "./marketing/context";
import { marketingApi, marketingKeys } from "./marketing/api";
import { approvalOf } from "./marketing/approval";
import { Loading, QueryError } from "./marketing/Common";
import { can } from "./marketing/model";
import { AppShell } from "./design/AppShell";
import { PageTitleContext } from "./design/pageMeta";
import { balanceHeadline, useBalanceSummary } from "./design/useBalance";
import { hasSleepingGuestsAccess, navigationGroups } from "./design/nav";
import { citiesLabel, companiesLabel } from "./design/shellLabels";
import { ROLE_LABEL, viewerRole } from "./marketing/viewerRole";
import { RouteBoundary } from "./design/RouteBoundary";
import {
  OnboardingProvider,
  useOnboarding,
} from "./design/onboarding/OnboardingContext";
import {
  authenticatedSmsSession,
  initialSmsSession,
  signedOutSmsSession,
  ssoTokenFromHash,
} from "./sessionState";

// Код экрана загружается при открытии; меню и шапка остаются доступными.
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const SegmentsPage = lazy(() => import("./pages/SegmentsPage"));
const GuestsPage = lazy(() => import("./pages/GuestsPage"));
const CampaignsPage = lazy(() => import("./marketing/CampaignsPage"));
const CampaignEditorPage = lazy(() => import("./marketing/CampaignEditorPage"));
const CampaignResultsPage = lazy(
  () => import("./marketing/CampaignResultsPage"),
);
const CampaignResultsRoute = lazy(
  () => import("./marketing/CampaignResultsRoute"),
);
// Пакет 7b: очередь «На согласовании» (п.23) и мастер запуска кампании (п.17).
const ApprovalsPage = lazy(() => import("./marketing/ApprovalsPage"));
const LaunchWizardPage = lazy(() => import("./marketing/LaunchWizardPage"));
const CampaignCatalogPage = lazy(
  () => import("./marketing/CampaignCatalogPage"),
);
const CampaignDashboardPage = lazy(
  () => import("./marketing/CampaignDashboardPage"),
);
const BillingPage = lazy(() => import("./marketing/BillingPage"));
const ClientsPage = lazy(() => import("./marketing/ClientsPage"));
const SpendingPage = lazy(() => import("./marketing/SpendingPage"));
const ChannelsPage = lazy(() => import("./marketing/ChannelsPage"));
const RecommendationsPage = lazy(
  () => import("./marketing/RecommendationsPage"),
);
const HelpPage = lazy(() => import("./marketing/HelpPage"));
// Пакет 8: «Главная» партнёра и отчёт «Согласия на рассылки».
const HomePage = lazy(() => import("./hub/HomePage"));
const ConsentCoveragePage = lazy(() => import("./hub/ConsentCoveragePage"));
const UpliftReportPage = lazy(() => import("./marketing/UpliftReportPage"));
const PromoPoolsPage = lazy(() => import("./marketing/PromoPoolsPage"));
const TemplateLibraryPage = lazy(
  () => import("./marketing/TemplateLibraryPage"),
);
const SettingsPage = lazy(() => import("./marketing/SettingsPage"));
const FinanceOverviewPage = lazy(
  () => import("./marketing/FinanceOverviewPage"),
);
const BusinessPage = lazy(() => import("./workspace/BusinessPage"));
const CohortsPage = lazy(() => import("./cohorts/CohortsPage"));
const DataPage = lazy(() => import("./workspace/DataPage"));
const GoalsPage = lazy(() => import("./workspace/GoalsPage"));
const ScenariosPage = lazy(() => import("./workspace/ScenariosPage"));
const IntegrationsPage = lazy(() =>
  import("./workspace/AdminPages").then((module) => ({
    default: module.IntegrationsPage,
  })),
);
const SecurityPage = lazy(() =>
  import("./workspace/AdminPages").then((module) => ({
    default: module.SecurityPage,
  })),
);
const AuditPage = lazy(() => import("./workspace/admin/AuditPage"));
const FormatsPage = lazy(() => import("./workspace/FormatsPage"));
const IdeasPage = lazy(() => import("./workspace/IdeasPage"));
const NotificationsPage = lazy(() => import("./workspace/NotificationsPage"));
const TargetActionsPage = lazy(() =>
  import("./workspace/AdminPages").then((module) => ({
    default: module.TargetActionsPage,
  })),
);
const SiteReportPage = lazy(() =>
  import("./workspace/AdminPages").then((module) => ({
    default: module.SiteReportPage,
  })),
);

/** /campaigns/new: каталог задач без параметров, редактор — с template/blank/channel/tour или копией из state. */
function NewCampaignRoute() {
  const [search] = useSearchParams();
  const location = useLocation();
  const copy = !!(location.state as { campaignCopy?: unknown } | null)
    ?.campaignCopy;
  const editor =
    copy ||
    ["template", "blank", "channel", "tour"].some((key) => search.has(key));
  return editor ? <CampaignEditorPage /> : <CampaignCatalogPage />;
}

/** Экран до лейаута (вход через CRM, загрузка кабинета, ошибка). */
function Boot({ children }: { children: React.ReactNode }) {
  return (
    <div className="marketing-root fk-boot">
      <div className="fk-boot-card">{children}</div>
    </div>
  );
}

export default function App() {
  const location = useLocation();
  const qc = useQueryClient();
  const [session, setSession] = useState(() =>
    initialSmsSession(getToken(), window.location.hash),
  );
  const [ssoToken] = useState(() => ssoTokenFromHash(window.location.hash));
  const ssoExchange = useRef<Promise<{ name: string }> | null>(null);
  const authed = session.authed;
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [titleOverride, setTitleOverride] = useState<string | null>(null);
  const titleContext = useMemo(
    () => ({ override: titleOverride, setOverride: setTitleOverride }),
    [titleOverride],
  );
  const acceptSession = useCallback(() => {
    qc.clear();
    setSession(authenticatedSmsSession);
    setSsoError(null);
  }, [qc]);
  useEffect(() => {
    if (!ssoToken) return;
    let active = true;
    // Один обмен для повторной подписки StrictMode; истёкший/зависший вход ограничен таймаутом API.
    if (!ssoExchange.current) {
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname + window.location.search,
      );
      ssoExchange.current = exchangeSso(ssoToken);
    }
    ssoExchange.current
      .then(() => {
        if (active) acceptSession();
      })
      .catch((error: unknown) => {
        if (!active) return;
        setSession(signedOutSmsSession);
        setSsoError(
          error instanceof Error
            ? error.message
            : "Ссылка входа не принята. Зайдите из CRM ещё раз.",
        );
      });
    return () => {
      active = false;
    };
  }, [ssoToken, acceptSession]);
  const bootstrap = useQuery({
    queryKey: [...marketingKeys.bootstrap, session.revision],
    queryFn: ({ signal }) => marketingApi.bootstrap(signal),
    enabled: authed,
  });
  const logout = () => {
    clearToken();
    qc.clear();
    setSession(signedOutSmsSession);
  };
  if (session.exchangingSso)
    return (
      <Boot>
        <Loading text="Входим через CRM…" />
      </Boot>
    );
  if (!authed) return <LoginScreen error={ssoError} onToken={acceptSession} />;
  if (bootstrap.isPending)
    return (
      <Boot>
        <Loading text="Открываем ваш кабинет…" />
      </Boot>
    );
  if (bootstrap.isError)
    return (
      <Boot>
        <QueryError
          error={bootstrap.error}
          retry={() => void bootstrap.refetch()}
        />
        <button className="mk-button" onClick={logout}>
          Войти заново
        </button>
      </Boot>
    );
  const data = bootstrap.data;
  const finance = can(data, "billing");
  // «Главная» (итог недели, баланс, готовые сценарии) — только кабинету партнёра.
  const partnerHome = viewerRole(data.scope) === "partner";
  // Реселлерский обзор «Клиенты» — только администратору управляющей компании.
  const clients = data.scope.isAdmin && finance;
  return (
    <MarketingContext.Provider value={data}>
      <PageTitleContext.Provider value={titleContext}>
        <OnboardingProvider bootstrap={data} sessionRevision={session.revision}>
          <Shell finance={finance} clients={clients} onLogout={logout}>
            <RouteBoundary key={location.pathname}>
              <Suspense fallback={<Loading text="Открываем раздел…" />}>
                <Routes>
                  <Route
                    path="/data/orders"
                    element={<DataPage key="orders" kind="orders" />}
                  />
                  <Route
                    path="/data/actions"
                    element={<DataPage key="actions" kind="actions" />}
                  />
                  <Route
                    path="/data/products"
                    element={<DataPage key="products" kind="products" />}
                  />
                  <Route path="/reports/business" element={<BusinessPage />} />
                  <Route
                    path="/reports/lifecycle"
                    element={<BusinessPage mode="lifecycle" />}
                  />
                  <Route
                    path="/reports/segments"
                    element={<BusinessPage mode="segments" />}
                  />
                  <Route path="/reports/cohorts" element={<CohortsPage />} />
                  <Route path="/reports/goals" element={<GoalsPage />} />
                  <Route
                    path="/reports/actions"
                    element={<TargetActionsPage />}
                  />
                  <Route
                    path="/reports/promotions"
                    element={<DataPage key="promotions" kind="promotions" />}
                  />
                  <Route
                    path="/reports/scenarios"
                    element={<ScenariosPage />}
                  />
                  <Route path="/reports/site" element={<SiteReportPage />} />
                  <Route path="/preferences" element={<FormatsPage />} />
                  <Route path="/problems" element={<IdeasPage />} />
                  <Route
                    path="/notifications"
                    element={<NotificationsPage />}
                  />
                  <Route
                    path="/settings/integrations"
                    element={
                      data.scope.isAdmin ? (
                        <IntegrationsPage />
                      ) : (
                        <Navigate to="/campaign-dashboard" replace />
                      )
                    }
                  />
                  <Route
                    path="/settings/staff"
                    element={
                      data.scope.isAdmin ? (
                        <SecurityPage staff />
                      ) : (
                        <Navigate to="/campaign-dashboard" replace />
                      )
                    }
                  />
                  <Route
                    path="/settings/security"
                    element={
                      data.scope.isAdmin ? (
                        <SecurityPage />
                      ) : (
                        <Navigate to="/campaign-dashboard" replace />
                      )
                    }
                  />
                  {/* Журнал действий открыт всем с доступом в SMS-центр: Go отдаёт каждому только его область. */}
                  <Route path="/settings/audit" element={<AuditPage />} />
                  <Route path="/audit" element={<AuditPage />} />
                  <Route
                    path="/"
                    element={
                      <Navigate
                        to={partnerHome ? "/home" : "/campaign-dashboard"}
                        replace
                      />
                    }
                  />
                  <Route
                    path="/home"
                    element={
                      partnerHome ? (
                        <HomePage />
                      ) : (
                        <Navigate to="/campaign-dashboard" replace />
                      )
                    }
                  />
                  <Route
                    path="/reports/consents"
                    element={<ConsentCoveragePage />}
                  />
                  <Route
                    path="/reports/uplift"
                    element={<UpliftReportPage />}
                  />
                  <Route
                    path="/campaign-dashboard"
                    element={<CampaignDashboardPage />}
                  />
                  <Route path="/campaigns" element={<CampaignsPage />} />
                  <Route
                    path="/push"
                    element={<Navigate to="/campaigns" replace />}
                  />
                  <Route path="/campaigns/new" element={<NewCampaignRoute />} />
                  {/* Мастер запуска за 3 шага (пакет 7b, п.17); статический путь объявлен раньше /campaigns/:id. */}
                  <Route path="/campaigns/wizard" element={<LaunchWizardPage />} />
                  <Route path="/approvals" element={<ApprovalsPage />} />
                  {/* Статический /campaigns/results объявлен раньше /campaigns/:id. */}
                  <Route
                    path="/campaigns/results"
                    element={<CampaignResultsRoute />}
                  />
                  <Route
                    path="/campaigns/:id"
                    element={<CampaignEditorPage />}
                  />
                  <Route
                    path="/campaigns/:id/results"
                    element={<CampaignResultsPage />}
                  />
                  <Route
                    path="/recommendations"
                    element={<RecommendationsPage />}
                  />
                  <Route
                    path="/spending"
                    element={
                      finance ? (
                        <SpendingPage />
                      ) : (
                        <Navigate to="/campaigns" replace />
                      )
                    }
                  />
                  <Route
                    path="/billing"
                    element={
                      finance ? (
                        <BillingPage />
                      ) : (
                        <Navigate to="/campaigns" replace />
                      )
                    }
                  />
                  <Route
                    path="/clients"
                    element={
                      clients ? (
                        <ClientsPage />
                      ) : (
                        <Navigate to="/campaign-dashboard" replace />
                      )
                    }
                  />
                  <Route path="/promo-codes" element={<PromoPoolsPage />} />
                  <Route
                    path="/channels"
                    element={
                      data.scope.isAdmin ? (
                        <ChannelsPage />
                      ) : (
                        <Navigate to="/campaign-dashboard" replace />
                      )
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      data.scope.isAdmin ? (
                        <SettingsPage />
                      ) : (
                        <Navigate to="/campaign-dashboard" replace />
                      )
                    }
                  />
                  <Route
                    path="/finance"
                    element={
                      finance ? (
                        <FinanceOverviewPage />
                      ) : (
                        <Navigate to="/campaign-dashboard" replace />
                      )
                    }
                  />
                  <Route path="/templates" element={<TemplateLibraryPage />} />
                  <Route
                    path="/templates/:id"
                    element={<TemplateLibraryPage />}
                  />
                  <Route
                    path="/integrations"
                    element={<Navigate to="/channels" replace />}
                  />
                  <Route
                    path="/analytics"
                    element={<DashboardPage />}
                  />
                  <Route
                    path="/segments"
                    element={<SegmentsPage />}
                  />
                  <Route
                    path="/guests"
                    element={<GuestsPage />}
                  />
                  <Route path="/howto" element={<HelpPage />} />
                  <Route
                    path="*"
                    element={<Navigate to="/campaign-dashboard" replace />}
                  />
                </Routes>
              </Suspense>
            </RouteBoundary>
          </Shell>
        </OnboardingProvider>
      </PageTitleContext.Provider>
    </MarketingContext.Provider>
  );
}

/** Меню и шапка: данные из bootstrap, баланс, колокольчик, кнопка обучения. */
function Shell({
  finance,
  clients,
  onLogout,
  children,
}: {
  finance: boolean;
  /** Пункт «Счета партнёров» — только администратору УК с правом на финансы. */
  clients: boolean;
  onLogout: () => void;
  children: React.ReactNode;
}) {
  const data = useMarketing();
  const onboarding = useOnboarding();
  const billing = useBalanceSummary(finance);
  const isAdmin = data.scope.isAdmin;
  // Партнёр — тот, у кого в токене есть юрлица (как Go isPartner и «Обзор
  // финансов»), а не любой без «*»: сотрудник УК баннер партнёра не видит.
  const role = viewerRole(data.scope);
  const isPartner = role === "partner";
  // Очередь «На согласовании» (пакет 7b, п.23): пункт меню и счётчик — только администратору УК, который решает.
  // Число — total очереди с сервера (легкий запрос countOnly, раз в минуту и после каждого решения), а не подсчёт по
  // списку кампаний: список обрезан 250 записями и обновляется реже.
  // Выключатель выключен — пункт виден, только пока в очереди что-то осталось (включали и выключили); опроса раз
  // в минуту нет, число берётся один раз при открытии.
  const approval = approvalOf(data);
  const canDecide = approval.canDecide;
  const approvalCount = useQuery({
    queryKey: [...marketingKeys.approvals, "count"],
    queryFn: ({ signal }) => marketingApi.approvalCount(signal),
    enabled: canDecide,
    staleTime: 30_000,
    refetchInterval: approval.enabled ? 60_000 : false,
    retry: false,
  });
  const approvalTotal = approvalCount.data?.total ?? 0;
  const pendingApprovals = canDecide && (approval.enabled || approvalTotal > 0) ? approvalTotal : null;
  const groups = navigationGroups({
    finance,
    clients,
    admin: isAdmin,
    partner: isPartner,
    approvals: pendingApprovals,
    // Ссылка на «Карту спящих гостей» в CRM — только с правом CRM на неё (в токене SSO).
    crmReports: hasSleepingGuestsAccess(data.scope.permissions),
  });
  const userName = getUserName();
  const company = companiesLabel(data.companies, isPartner ? data.partners : []);
  const balance = finance
    ? balanceHeadline(billing.response, { network: !isPartner, clients })
    : null;
  return (
    <AppShell
      groups={groups}
      user={{
        name: userName,
        roleLabel: ROLE_LABEL[role],
        company:
          data.companies.length > 2
            ? `Юридических лиц: ${data.companies.length}`
            : company || undefined,
      }}
      balance={balance}
      partner={
        isPartner
          ? {
              name: company || userName || "партнёр",
              place: citiesLabel(data.tenants, data.scope.partnerIds ?? []),
            }
          : null
      }
      onLogout={onLogout}
      onStartTour={onboarding.startTour}
      tourDone={onboarding.tourDoneLocally}
    >
      {children}
    </AppShell>
  );
}
